import React, { useState, useEffect, useContext, useMemo } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit2, Trash2, Building2, Star, X, Check, Users } from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import ProcesadorasCobroPanel from "../components/ProcesadorasCobroPanel";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, moneda = "PYG") => {
  // Protección contra null, undefined y NaN
  const raw = Number(n);
  const num = (n === null || n === undefined || isNaN(raw)) ? 0 : raw;
  if (moneda === "USD") return `$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (moneda === "BRL") return `R$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₲ ${Math.round(num).toLocaleString("es-PY")}`;
};

const emptyForm = {
  nombre: "",
  banco: "",
  numero_cuenta: "",
  moneda: "PYG",
  saldo_inicial: "",
  saldo_inicial_fecha: "",
  es_predeterminada: false,
  permite_saldo_negativo: false,
  notas: "",
};

export default function BancosPage() {
  const navigate = useNavigate();
  const { token, activeEmpresaPropia, user, hasPermission } = useContext(AuthContext);
  const canCreate = user?.role === "admin" || hasPermission("bancos.crear");
  const canEdit = user?.role === "admin" || hasPermission("bancos.editar");
  const canDelete = user?.role === "admin" || hasPermission("bancos.eliminar");
  // Solo admin/gerente o quien tenga el permiso específico pueden asignar cuentas a usuarios.
  // bancos.editar NO da acceso: es para editar datos de la cuenta, no para asignar acceso al reporte.
  const canAccesoReporte = user?.role === "admin" || user?.role === "gerente"
    || hasPermission("configuracion_financiero.gestionar_acceso_bancos");

  const [cuentas, setCuentas] = useState([]);
  const [balanceTotales, setBalanceTotales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showAccesoModal, setShowAccesoModal] = useState(false);
  const [cuentaAcceso, setCuentaAcceso] = useState(null);
  const [usuariosReporte, setUsuariosReporte] = useState([]);
  const [accesoIds, setAccesoIds] = useState([]);
  const [loadingAcceso, setLoadingAcceso] = useState(false);
  const [accesoAviso, setAccesoAviso] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCuentas = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const qs = q.toString() ? `?${q}` : "";
      const [resLista, resSaldos] = await Promise.all([
        fetch(`${API}/admin/cuentas-bancarias${qs}`, { headers }),
        fetch(`${API}/admin/cuentas-bancarias/saldos${qs}`, { headers }),
      ]);
      const lista = resLista.ok ? await resLista.json() : [];
      const saldos = resSaldos.ok ? await resSaldos.json() : [];
      const saldoMap = Object.fromEntries((saldos || []).map(c => [c.id, c]));
      const merged = (lista.length ? lista : saldos).map(c => ({
        ...c,
        ...(saldoMap[c.id] || {}),
        saldo_actual: saldoMap[c.id]?.saldo_actual ?? c.saldo_actual ?? c.saldo_inicial ?? 0,
      }));
      if (!merged.length && !activeEmpresaPropia?.slug) {
        toast.error("Seleccioná una empresa propia en el selector superior");
      }
      setCuentas(merged);
    } catch { toast.error("Error al cargar cuentas"); }
    setLoading(false);
  };

  const fetchBalanceTotales = async () => {
    try {
      const hoy = new Date();
      const periodo = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
      const q = new URLSearchParams({ periodo });
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/balance?${q}`, { headers });
      if (res.ok) setBalanceTotales(await res.json());
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchCuentas();
    fetchBalanceTotales();
  }, [activeEmpresaPropia]); // eslint-disable-line

  const openNew = () => {
    if (!activeEmpresaPropia?.slug) {
      toast.error("Seleccioná una empresa propia en el selector superior antes de crear la cuenta");
      return;
    }
    setEditingId(null);
    setFormData({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setFormData({
      nombre: c.nombre || "",
      banco: c.banco || "",
      numero_cuenta: c.numero_cuenta || "",
      moneda: c.moneda || "PYG",
      // Usar != null para no descartar el valor 0 con || ""
      saldo_inicial: c.saldo_inicial != null ? String(c.saldo_inicial) : "",
      saldo_inicial_fecha: c.saldo_inicial_fecha || "",
      es_predeterminada: !!c.es_predeterminada,
      permite_saldo_negativo: !!c.permite_saldo_negativo,
      notas: c.notas || "",
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.nombre) { toast.error("Nombre requerido"); return; }
    const slug = activeEmpresaPropia?.slug;
    if (!slug) {
      toast.error("Seleccioná una empresa propia en el selector superior");
      return;
    }
    try {
      const payload = {
        ...formData,
        saldo_inicial: parseFloat(formData.saldo_inicial) || 0,
        logo_tipo: slug,
      };
      const url = editingId
        ? `${API}/admin/cuentas-bancarias/${editingId}`
        : `${API}/admin/cuentas-bancarias`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      toast.success(editingId ? "Cuenta actualizada" : `Cuenta creada (${activeEmpresaPropia?.nombre || slug})`);
      setShowForm(false);
      await fetchCuentas();
    } catch (err) {
      toast.error(err.message || "Error al guardar");
    }
  };

  const openAccesoReporte = async (c) => {
    if (!canAccesoReporte) return;
    setCuentaAcceso(c);
    setAccesoIds((c.usuarios_reporte_ids || []).map(String));
    setShowAccesoModal(true);
    setLoadingAcceso(true);
    setAccesoAviso("");
    try {
      const q = new URLSearchParams();
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      q.set("cuenta_id", c.id);
      const res = await fetch(`${API}/admin/cuentas-bancarias/usuarios-acceso-reporte?${q}`, { headers });
      if (res.ok) {
        const data = await res.json();
        const lista = Array.isArray(data) ? data : (data.usuarios || []);
        setUsuariosReporte(lista);
        setAccesoAviso(data.aviso || "");
        if (!lista.length && data.aviso) toast.error(data.aviso, { duration: 8000 });
      } else {
        const err = await res.json().catch(() => ({}));
        setUsuariosReporte([]);
        toast.error(err.detail || "No se pudo cargar usuarios");
      }
    } catch {
      setUsuariosReporte([]);
      toast.error("Error de conexión al cargar usuarios");
    }
    setLoadingAcceso(false);
  };

  const toggleAccesoUsuario = (uid) => {
    const id = String(uid);
    setAccesoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const guardarAccesoReporte = async () => {
    if (!cuentaAcceso?.id) return;
    try {
      const res = await fetch(`${API}/admin/cuentas-bancarias/${cuentaAcceso.id}/acceso-reporte`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ usuarios_reporte_ids: accesoIds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al guardar");
      }
      toast.success("Acceso al reporte actualizado");
      setShowAccesoModal(false);
      await fetchCuentas();
    } catch (e) {
      toast.error(e.message || "Error al guardar acceso");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar la cuenta "${c.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/admin/cuentas-bancarias/${c.id}`, { method: "DELETE", headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      toast.success("Cuenta eliminada");
      fetchCuentas();
    } catch (err) {
      toast.error(err.message || "Error al eliminar la cuenta");
    }
  };

  // Totales reales: suma de saldo_actual de cada cuenta por moneda
  const totalPYG = cuentas.filter(c => c.moneda === "PYG").reduce((s, c) => s + (c.saldo_actual || 0), 0);
  const totalUSD = cuentas.filter(c => c.moneda === "USD").reduce((s, c) => s + (c.saldo_actual || 0), 0);
  const totalBRL = cuentas.filter(c => c.moneda === "BRL").reduce((s, c) => s + (c.saldo_actual || 0), 0);

  const hayModalAbierto = !!(showForm || showAccesoModal);
  useAutoRefresh(fetchCuentas, 30_000, !hayModalAbierto);

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/sistema/financiero")} data-testid="back-btn"
            className="text-slate-400 hover:text-white p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-white font-bold">Bancos y Cuentas</h1>
              <p className="text-slate-400 text-sm">Saldo por cuenta · PYG y USD</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {activeEmpresaPropia && (
              <span className="text-xs px-3 py-1 rounded-full border"
                style={{ backgroundColor: `${activeEmpresaPropia.color || "#3b82f6"}15`, borderColor: `${activeEmpresaPropia.color || "#3b82f6"}40`, color: activeEmpresaPropia.color || "#3b82f6" }}>
                {activeEmpresaPropia.nombre}
              </span>
            )}
            {canCreate && (
              <button onClick={openNew} data-testid="new-cuenta-btn"
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                <Plus className="w-4 h-4" /> Nueva cuenta
              </button>
            )}
          </div>
        </div>

        {/* Totales */}
        <div className={`grid grid-cols-1 gap-4 mb-6 ${(totalBRL !== 0 || activeEmpresaPropia?.monedas_habilitadas?.includes("BRL")) ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
            <p className="text-emerald-300 text-xs uppercase tracking-wider font-body">Saldo total PYG</p>
            <p className="text-white font-heading text-2xl mt-1">{fmt(totalPYG, "PYG")}</p>
            <p className="text-slate-400 text-xs mt-1">{cuentas.filter(c => c.moneda === "PYG").length} cuenta(s)</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <p className="text-blue-300 text-xs uppercase tracking-wider font-body">Saldo total USD</p>
            <p className="text-white font-heading text-2xl mt-1">{fmt(totalUSD, "USD")}</p>
            <p className="text-slate-400 text-xs mt-1">{cuentas.filter(c => c.moneda === "USD").length} cuenta(s)</p>
          </div>
          {(totalBRL !== 0 || activeEmpresaPropia?.monedas_habilitadas?.includes("BRL")) && (
            <div className="bg-green-700/10 border border-green-700/20 rounded-xl p-4">
              <p className="text-green-300 text-xs uppercase tracking-wider font-body">Saldo total BRL</p>
              <p className="text-white font-heading text-2xl mt-1">{fmt(totalBRL, "BRL")}</p>
              <p className="text-slate-400 text-xs mt-1">{cuentas.filter(c => c.moneda === "BRL").length} cuenta(s)</p>
            </div>
          )}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="text-center py-12 text-slate-500 animate-pulse">Cargando...</div>
        ) : cuentas.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-xl">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400 font-body">Sin cuentas bancarias</p>
            {canCreate && <button onClick={openNew} className="text-blue-400 hover:text-blue-300 text-sm mt-3 underline">Crear la primera cuenta</button>}
            {!activeEmpresaPropia?.slug && (
              <p className="text-amber-400/90 text-xs mt-2">Seleccioná la empresa propia arriba para ver y crear cuentas de esa empresa.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {cuentas.map(c => (
              <div key={c.id} data-testid={`cuenta-${c.id}`}
                className="group flex items-center gap-4 p-4 bg-white/3 hover:bg-white/5 border border-white/10 rounded-xl transition-all">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  c.moneda === "USD" ? "bg-blue-500/15 text-blue-400" : c.moneda === "BRL" ? "bg-green-700/15 text-green-400" : "bg-emerald-500/15 text-emerald-400"
                }`}>
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-body text-white font-medium">{c.nombre}</p>
                    {c.es_predeterminada && (
                      <span className="text-[10px] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                        <Star className="w-2.5 h-2.5" /> Predeterminada
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-xs">
                    {c.banco || "—"}{c.numero_cuenta ? ` · N° ${c.numero_cuenta}` : ""}
                  </p>
                  {canAccesoReporte && (c.usuarios_reporte_ids?.length > 0) && (
                    <p className="text-cyan-500/80 text-[10px] mt-0.5">
                      Reporte: {c.usuarios_reporte_ids.length} usuario(s)
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className={`font-heading text-lg font-bold ${(c.saldo_actual ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {fmt(c.saldo_actual, c.moneda)}
                  </p>
                  <p className="text-slate-500 text-[10px]">saldo actual</p>
                  {c.saldo_inicial != null && c.saldo_inicial !== c.saldo_actual && (
                    <p className="text-slate-600 text-[10px]">inicial: {fmt(c.saldo_inicial, c.moneda)}</p>
                  )}
                </div>
                {(canEdit || canAccesoReporte) && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canAccesoReporte && (
                      <button type="button" onClick={() => openAccesoReporte(c)} title="Acceso reporte Caja/Banco"
                        className="text-slate-400 hover:text-cyan-400 p-1">
                        <Users className="w-4 h-4" />
                      </button>
                    )}
                    {canEdit && (
                    <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-blue-400 p-1">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    )}
                    {canDelete && <button onClick={() => handleDelete(c)} className="text-slate-400 hover:text-red-400 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <ProcesadorasCobroPanel API={API} token={token} logoTipo={activeEmpresaPropia?.slug}
          cuentas={cuentas} puedeGestionar={user?.role === "admin" || hasPermission("bancos.gestionar_procesadoras")} />
      </div>

      {showAccesoModal && cuentaAcceso && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div onClick={e => e.stopPropagation()} className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <h2 className="font-heading text-white text-lg">Acceso reporte Caja/Banco</h2>
                <p className="text-slate-500 text-xs mt-1">{cuentaAcceso.nombre}</p>
              </div>
              <button type="button" onClick={() => setShowAccesoModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              <p className="text-slate-400 text-xs mb-4">
                Marcá los usuarios que pueden generar el reporte de esta cuenta.
                Solo aparecen usuarios con permiso <span className="text-cyan-400 font-medium">Reportes → Caja/Banco</span>.
                Los usuarios marcados <strong className="text-white">solo verán esta cuenta</strong> en su reporte, no el resto de las cuentas.
              </p>
              {loadingAcceso ? (
                <p className="text-slate-500 text-sm animate-pulse">Cargando usuarios…</p>
              ) : usuariosReporte.length === 0 ? (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-amber-300 text-xs leading-relaxed">
                    {accesoAviso || (
                      <>
                        No hay usuarios con el permiso <strong>Reportes → Caja/Banco</strong>.<br />
                        Andá a <strong>Usuarios y Permisos</strong>, buscá al usuario, activá el permiso
                        y guardá. Luego el usuario debe cerrar sesión y volver a entrar.
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {usuariosReporte.map(u => {
                    const on = accesoIds.includes(String(u.id));
                    return (
                      <button key={u.id} type="button" onClick={() => toggleAccesoUsuario(u.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-all flex items-center justify-between gap-2 ${on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" : "border-white/10 text-slate-300 hover:border-white/30 hover:bg-white/3"}`}>
                        <div>
                          <span className="font-medium block">{u.name}</span>
                          <span className="text-slate-500 text-xs">{u.email}</span>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${on ? "bg-cyan-500 border-cyan-500" : "border-white/20"}`}>
                          {on && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-white/10 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAccesoModal(false)} className="px-4 py-2 text-slate-400 text-sm">Cancelar</button>
              <button type="button" onClick={guardarAccesoReporte} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()}
            className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-white text-lg">{editingId ? "Editar cuenta" : "Nueva cuenta bancaria"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-slate-400 text-xs block mb-1">Nombre *</label>
                <input required value={formData.nombre} onChange={e => setFormData(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Ej: Cuenta principal PYG"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Banco</label>
                  <input value={formData.banco} onChange={e => setFormData(f => ({ ...f, banco: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Itaú, Continental, …"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">N° cuenta</label>
                  <input value={formData.numero_cuenta} onChange={e => setFormData(f => ({ ...f, numero_cuenta: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                    placeholder="123-456-789"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Moneda *</label>
                  <select value={formData.moneda} onChange={e => setFormData(f => ({ ...f, moneda: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
                    <option value="PYG">₲ PYG</option>
                    <option value="USD">$ USD</option>
                    {(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") || formData.moneda === "BRL") && (
                      <option value="BRL">R$ BRL</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Saldo inicial</label>
                  <input type="number" step="any" value={formData.saldo_inicial}
                    onChange={e => setFormData(f => ({ ...f, saldo_inicial: e.target.value }))}
                    disabled={!!editingId}
                    className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 ${editingId ? "opacity-50 cursor-not-allowed" : ""}`}
                    placeholder="0"
                  />
                  {editingId && <p className="text-xs text-slate-500 mt-1">El saldo inicial no se puede modificar una vez creada la cuenta.</p>}
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Desde fecha</label>
                  <DateInput value={formData.saldo_inicial_fecha || ""}
                    onChange={e => setFormData(f => ({ ...f, saldo_inicial_fecha: e.target.value }))}
                    disabled={!!editingId}
                    className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 ${editingId ? "opacity-50 cursor-not-allowed" : ""}`}
                  />
                  {editingId && <p className="text-xs text-slate-500 mt-1">El saldo inicial no se puede modificar una vez creada la cuenta.</p>}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={formData.es_predeterminada}
                  onChange={e => setFormData(f => ({ ...f, es_predeterminada: e.target.checked }))}
                  className="accent-amber-500"
                />
                <span className="text-slate-300 text-sm inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400" /> Predeterminada para esta moneda</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.permite_saldo_negativo}
                  onChange={e => setFormData(f => ({ ...f, permite_saldo_negativo: e.target.checked }))}
                  className="accent-amber-500"
                />
                <span className="text-slate-300 text-sm">Permite saldo negativo</span>
                <span className="text-slate-500 text-xs">(las transferencias pueden dejar la cuenta en negativo)</span>
              </label>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Notas</label>
                <textarea value={formData.notas} onChange={e => setFormData(f => ({ ...f, notas: e.target.value }))}
                  rows="2"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">Cancelar</button>
              <button type="submit" data-testid="save-cuenta-btn"
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm inline-flex items-center justify-center gap-1">
                <Check className="w-4 h-4" /> {editingId ? "Guardar" : "Crear cuenta"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
